"use client"

import { useState, useEffect, useCallback } from "react"
import { useTranslations, useLocale } from "next-intl"
import Link from "next/link"
import { getConsent, setConsent, COOKIE_CATEGORIES } from "@/lib/cookies/consent"
import type { ConsentCategories } from "@/lib/cookies/consent"
import { Button } from "@/components/ui/button"
import { getLegalSlug } from "@/lib/legal/slugs"
import type { Locale } from "@/lib/legal/slugs"

// ---------------------------------------------------------------------------
// CookieConsent — fixed bottom banner for EU cookie consent (GDPR)
//
// Behavior:
// - Initial state: hidden (avoids hydration flash for returning visitors)
// - On mount: reads consent cookie. Shows banner if none exists.
// - Accept All: sets all categories true, hides banner
// - Manage Preferences: opens inline panel with category toggles
// - Save Preferences: sets selected categories, hides banner
// - Escape: closes preferences panel (not the banner)
// ---------------------------------------------------------------------------

// Announce a consent change so client-side, consent-gated integrations (e.g. the
// GoogleAnalytics Consent Mode component) can react in-place without a reload.
// `getConsent()` reads the cookie synchronously, so listeners see the new state.
function notifyConsentChanged() {
  if (typeof window === "undefined") return
  window.dispatchEvent(new CustomEvent("cookie-consent-changed"))
}

export default function CookieConsent() {
  const t = useTranslations("cookieConsent")
  const locale = useLocale() as Locale
  const privacySlug = getLegalSlug("privacy", locale)
  const cookiePolicyHref = `/${locale}/legal/${privacySlug}`

  // null = not yet determined (SSR / pre-hydration), false = no consent needed,
  // true = show banner
  const [showBanner, setShowBanner] = useState<boolean>(false)
  const [showPanel, setShowPanel] = useState(false)
  const [categories, setCategories] = useState<ConsentCategories>({
    essential: true,
    analytics: false,
    marketing: false,
  })

  // On mount: check for existing consent — show banner only if absent.
  // This runs client-side only, preventing hydration mismatch.
  // setState in useEffect is intentional here: we're syncing with
  // document.cookie (external browser API) which is unavailable server-side.
  useEffect(() => {
    const existing = getConsent()
    if (!existing) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowBanner(true)
    }
  }, [])

  // Escape key handler attached to document — avoids non-interactive element interaction lint
  useEffect(() => {
    if (!showPanel) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault()
        setShowPanel(false)
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [showPanel])

  const handleAcceptAll = useCallback(() => {
    setConsent({ essential: true, analytics: true, marketing: true })
    notifyConsentChanged()
    setShowBanner(false)
    setShowPanel(false)
  }, [])

  const handleRejectAll = useCallback(() => {
    setConsent({ essential: true, analytics: false, marketing: false })
    notifyConsentChanged()
    setShowBanner(false)
    setShowPanel(false)
  }, [])

  const handleManagePreferences = useCallback(() => {
    setShowPanel((prev) => !prev)
  }, [])

  const handleSavePreferences = useCallback(() => {
    setConsent(categories)
    notifyConsentChanged()
    setShowBanner(false)
    setShowPanel(false)
  }, [categories])

  const handleToggleCategory = useCallback((id: keyof ConsentCategories) => {
    if (id === "essential") return // Essential is always on
    setCategories((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  if (!showBanner) return null

  return (
    <div
      role="region"
      aria-label={t("title")}
      aria-live="polite"
      className="fixed bottom-0 inset-x-0 z-50 bg-background border-t border-border shadow-lg pb-[env(safe-area-inset-bottom)]"
    >
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
        {/* Banner header row */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground">{t("title")}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t("description")}</p>
            <Link
              href={cookiePolicyHref}
              className="mt-1 inline-block text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              {t("cookiePolicy")}
            </Link>
          </div>
          <div className="flex flex-shrink-0 flex-col items-end gap-2">
            <div className="flex gap-2 max-[390px]:flex-col">
              <Button size="lg" className="min-h-11" onClick={handleAcceptAll}>
                {t("acceptAll")}
              </Button>
              <Button variant="outline" size="lg" className="min-h-11" onClick={handleRejectAll}>
                {t("rejectAll")}
              </Button>
            </div>
            <button
              onClick={handleManagePreferences}
              className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              {t("managePreferences")}
            </button>
          </div>
        </div>

        {/* Preferences panel — inline expansion */}
        {showPanel && (
          <div className="mt-4 border-t border-border pt-4">
            <ul className="space-y-4">
              {COOKIE_CATEGORIES.map((category) => (
                <li key={category.id} className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <label
                      htmlFor={`consent-${category.id}`}
                      className="text-sm font-medium text-foreground cursor-pointer"
                    >
                      {t(`${category.id}Label` as Parameters<typeof t>[0])}
                    </label>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t(`${category.id}Description` as Parameters<typeof t>[0])}
                    </p>
                  </div>
                  <div className="flex-shrink-0 pt-0.5">
                    <input
                      id={`consent-${category.id}`}
                      type="checkbox"
                      checked={category.id === "essential" ? true : categories[category.id]}
                      disabled={category.required}
                      onChange={() => handleToggleCategory(category.id)}
                      className="h-5 w-5 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex justify-end">
              <Button size="lg" className="min-h-11" onClick={handleSavePreferences}>
                {t("savePreferences")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
